"use client";

import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useState,
} from "react";

import { useSession } from "@/providers/SessionProvider";
import { useShowFavoriteID } from "@/services/hooks/useShowFavorite";

type FavoriteContextType = {
  favorites: Set<string>;
  toggleFavorite: (id: string) => void;
  isInitialized: boolean;
};

const FavoriteContext = createContext<FavoriteContextType | undefined>(
  undefined,
);

export const useFavorites = () => {
  const context = useContext(FavoriteContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoriteProvider");
  }
  return context;
};

export const FavoriteProvider = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode }
>(({ children }, ref) => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const { isAuthenticated } = useSession();

  const { dataFavoriteID } = useShowFavoriteID(isAuthenticated);

  useEffect(() => {
    try {
      const storedFavorites = JSON.parse(
        localStorage.getItem("FavoriteProperty") || "[]",
      );
      setFavorites(new Set(storedFavorites));

      // Validate stored IDs against backend to remove deleted properties
      // This is done only for non-authenticated users to avoid an extra
      // request for authorised sessions where IDs are managed server-side.
      (async () => {
        if (
          !isAuthenticated &&
          Array.isArray(storedFavorites) &&
          storedFavorites.length
        ) {
          try {
            // We request all objects in a single call (limit = storedFavorites.length)
            // The backend will silently ignore non-existent IDs, allowing us to
            // derive the list of valid IDs from the response.
            const response = await import("@/services/user.service").then((m) =>
              m.userService.showFavoritePropertyWithoutAuth({
                ids: storedFavorites,
                limit: storedFavorites.length,
              }),
            );

            const validIds: string[] = Array.isArray(response?.data)
              ? response.data.map((item: { id: string }) => item.id)
              : [];

            if (validIds.length !== storedFavorites.length) {
              // Update localStorage and context only if there is a difference
              localStorage.setItem(
                "FavoriteProperty",
                JSON.stringify(validIds),
              );
              setFavorites(new Set(validIds));
            }
          } catch (err) {
            console.error("Failed to validate favorite IDs", err);
          }
        }
      })();
    } catch (error) {
      console.error("Error loading favorites:", error);
    } finally {
      setIsInitialized(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && dataFavoriteID?.ids) {
      // Use server-side list exclusively and remove any client cache
      const serverFavorites = dataFavoriteID.ids as string[];
      setFavorites(new Set(serverFavorites));
      localStorage.removeItem("FavoriteProperty");
    } else if (!isAuthenticated) {
      // For guests we rely on cached ids in localStorage
      try {
        const stored = JSON.parse(
          localStorage.getItem("FavoriteProperty") || "[]",
        );
        setFavorites(new Set(stored));
      } catch (e) {
        console.error(e);
        setFavorites(new Set());
      }
    }
  }, [isAuthenticated, dataFavoriteID]);

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const updatedFavorites = new Set(prev);
      if (updatedFavorites.has(id)) {
        updatedFavorites.delete(id);
      } else {
        updatedFavorites.add(id);
      }
      if (!isAuthenticated) {
        localStorage.setItem(
          "FavoriteProperty",
          JSON.stringify([...updatedFavorites]),
        );
      }
      return updatedFavorites;
    });
  };

  return (
    <FavoriteContext.Provider
      value={{ favorites, toggleFavorite, isInitialized }}
    >
      <div ref={ref}>{children}</div>
    </FavoriteContext.Provider>
  );
});

FavoriteProvider.displayName = "FavoriteProvider";
